import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  TruckIcon, Package, MapPin, CheckCircle, LogOut, AlertOctagon,
  DollarSign, FileSignature, Download, CircleCheck, ChevronRight,
  Warehouse, Navigation, PackageCheck, Undo2, Clock, FileText,
  Phone, RefreshCw, CreditCard, Eye
} from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const distApi = axios.create({ baseURL: API_URL });
distApi.interceptors.request.use(config => {
  const token = localStorage.getItem('dist_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
distApi.interceptors.response.use(r => r, error => {
  if (error.response?.status === 401) {
    localStorage.removeItem('dist_token');
    localStorage.removeItem('distribuidor');
    window.location.href = '/distribuidor/login';
  }
  return Promise.reject(error);
});

const ESTADOS = [
  { value: 'almacen_rd', label: 'Almacen', icon: <Warehouse className="h-4 w-4" />, color: 'bg-purple-100 text-purple-800' },
  { value: 'en_ruta', label: 'En Ruta', icon: <Navigation className="h-4 w-4" />, color: 'bg-orange-100 text-orange-800' },
  { value: 'entregada', label: 'Entregada', icon: <PackageCheck className="h-4 w-4" />, color: 'bg-green-100 text-green-800' },
  { value: 'devuelta', label: 'Devuelta', icon: <Undo2 className="h-4 w-4" />, color: 'bg-red-100 text-red-800' },
  { value: 'pendiente_articulo', label: 'Art. Pend.', icon: <Clock className="h-4 w-4" />, color: 'bg-amber-100 text-amber-800' },
];

const DistribuidorPortal = () => {
  const navigate = useNavigate();
  const [dist, setDist] = useState(null);
  const [furgones, setFurgones] = useState([]);
  const [selectedFurgon, setSelectedFurgon] = useState(null);
  const [furgonData, setFurgonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Dialogs
  const [cobrarDialog, setCobrarDialog] = useState({ open: false, factura: null });
  const [cobrarForm, setCobrarForm] = useState({ monto: 0, metodo_pago_rd: 'efectivo_rd' });
  const [contratoDialog, setContratoDialog] = useState(false);
  const [pagoDialog, setPagoDialog] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('dist_token');
    if (!token) { navigate('/distribuidor/login'); return; }
    const stored = localStorage.getItem('distribuidor');
    if (stored) setDist(JSON.parse(stored));
    fetchFurgones();
  }, [navigate]);

  const fetchFurgones = async () => {
    setLoading(true);
    try {
      const [meRes, fRes] = await Promise.all([
        distApi.get('/distribuidor/me'),
        distApi.get('/distribuidor/mis-furgones')
      ]);
      setDist(meRes.data);
      setFurgones(fRes.data);
    } catch { toast.error('Error cargando datos'); }
    setLoading(false);
  };

  const fetchFurgonDetail = useCallback(async (numero) => {
    setLoadingDetail(true);
    try {
      const res = await distApi.get(`/distribuidor/furgon/${numero}`);
      setFurgonData(res.data);
    } catch { toast.error('Error cargando furgon'); }
    setLoadingDetail(false);
  }, []);

  const selectFurgon = (numero) => {
    setSelectedFurgon(numero);
    fetchFurgonDetail(numero);
  };

  const goBack = () => { setSelectedFurgon(null); setFurgonData(null); };

  const handleLogout = () => {
    localStorage.removeItem('dist_token');
    localStorage.removeItem('distribuidor');
    navigate('/distribuidor/login');
  };

  const cambiarEstado = async (facturaId, estado) => {
    try {
      await distApi.patch(`/distribuidor/factura/${facturaId}/estado`, { estado_distribucion: estado });
      toast.success('Estado actualizado');
      fetchFurgonDetail(selectedFurgon);
    } catch (error) { toast.error(error.response?.data?.detail || 'Error'); }
  };

  const handleCobrar = async () => {
    if (!cobrarDialog.factura || cobrarForm.monto <= 0) return;
    try {
      const res = await distApi.post(`/distribuidor/factura/${cobrarDialog.factura.id}/cobrar-rd`, cobrarForm);
      toast.success(res.data.message);
      setCobrarDialog({ open: false, factura: null });
      fetchFurgonDetail(selectedFurgon);
    } catch (error) { toast.error(error.response?.data?.detail || 'Error'); }
  };

  const handleDescargarContrato = async () => {
    try {
      const res = await distApi.get(`/distribuidor/furgon/${selectedFurgon}/contrato-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `contrato_${selectedFurgon}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Error descargando contrato'); }
  };

  const handleFirmarContrato = async () => {
    try {
      const res = await distApi.post(`/distribuidor/furgon/${selectedFurgon}/firmar-contrato`);
      toast.success(res.data.message);
      setContratoDialog(false);
      fetchFurgonDetail(selectedFurgon);
    } catch (error) { toast.error(error.response?.data?.detail || 'Error'); }
  };

  const getEstadoInfo = (v) => ESTADOS.find(e => e.value === v) || ESTADOS[0];

  // ============== RENDER ==============

  if (loading) return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-400" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50" data-testid="dist-portal">
      {/* HEADER */}
      <div className="bg-gradient-to-r from-indigo-700 to-purple-700 text-white px-4 py-3 sticky top-0 z-50">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TruckIcon className="h-6 w-6" />
            <div>
              <p className="font-bold text-sm leading-tight">{dist?.nombre || 'Distribuidor'}</p>
              <p className="text-indigo-200 text-xs">{dist?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {selectedFurgon && (
              <Button variant="ghost" size="sm" onClick={goBack} className="text-white/70 hover:text-white h-8 text-xs">
                Furgones
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white/70 hover:text-white h-8" data-testid="dist-logout">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4">
        {!selectedFurgon ? (
          /* ============== FURGONES LIST ============== */
          <>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-black text-gray-900">Mis Furgones</h2>
              <Button variant="outline" size="sm" onClick={fetchFurgones} className="h-8 gap-1">
                <RefreshCw className="h-3.5 w-3.5" /> Actualizar
              </Button>
            </div>

            {furgones.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
                <TruckIcon className="h-14 w-14 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-semibold">No tiene furgones asignados</p>
                <p className="text-gray-400 text-sm">Contacte al administrador</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="dist-furgones-list">
                {furgones.map(f => (
                  <button key={f.numero} onClick={() => selectFurgon(f.numero)}
                    data-testid={`dist-furgon-${f.numero}`}
                    className="w-full bg-white rounded-2xl border-2 border-gray-100 p-4 hover:border-indigo-300 hover:shadow-lg transition-all text-left active:scale-[0.98]">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                          <TruckIcon className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-black text-gray-900">{f.numero}</p>
                          <p className="text-xs text-gray-400">{f.numero_contenedor || 'Sin contenedor'}</p>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-300" />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs">{f.shipment_count} envios</Badge>
                      {f.contrato_firmado ? (
                        <Badge className="bg-green-50 text-green-700 border-green-200 text-xs gap-1">
                          <CircleCheck className="h-3 w-3" /> Contrato OK
                        </Badge>
                      ) : (
                        <Badge className="bg-red-50 text-red-700 border-red-200 text-xs gap-1 animate-pulse">
                          <AlertOctagon className="h-3 w-3" /> Firmar Contrato
                        </Badge>
                      )}
                      {f.mi_pago && (
                        <Badge className={`text-xs ${f.mi_pago.estado === 'pagado' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                          ${(f.mi_pago.total || 0).toFixed(2)} {f.mi_pago.estado === 'pagado' ? 'Pagado' : 'Pendiente'}
                        </Badge>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : loadingDetail ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
          </div>
        ) : furgonData && (
          /* ============== FURGON DETAIL ============== */
          <>
            {/* Furgon info */}
            <div className="bg-white rounded-2xl border-2 border-indigo-100 p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="text-xl font-black text-gray-900">{furgonData.furgon.numero}</h2>
                  <p className="text-xs text-gray-400">
                    {furgonData.furgon.numero_contenedor || 'Sin contenedor'} | B/L: {furgonData.furgon.numero_bl || 'Pendiente'}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => fetchFurgonDetail(selectedFurgon)} className="h-8">
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>
              </div>

              {/* Contract + actions */}
              <div className="flex flex-wrap gap-2">
                {furgonData.contrato.firmado ? (
                  <Badge className="bg-green-50 text-green-700 border-green-300 gap-1" data-testid="contrato-badge-ok">
                    <CircleCheck className="h-3.5 w-3.5" /> Contrato Firmado
                  </Badge>
                ) : (
                  <Badge className="bg-red-50 text-red-700 border-red-300 gap-1 animate-pulse" data-testid="contrato-badge-pending">
                    <AlertOctagon className="h-3.5 w-3.5" /> Contrato Pendiente
                  </Badge>
                )}
                <Button variant="outline" size="sm" onClick={() => setContratoDialog(true)}
                  className="h-7 text-xs border-indigo-300 text-indigo-700" data-testid="btn-ver-contrato">
                  <FileSignature className="h-3 w-3 mr-1" /> Contrato
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPagoDialog(true)}
                  className="h-7 text-xs border-purple-300 text-purple-700" data-testid="btn-ver-pago">
                  <CreditCard className="h-3 w-3 mr-1" /> Mi Pago
                </Button>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-2">
              <MiniStat label="Total" value={furgonData.stats.total} color="indigo" />
              <MiniStat label="Entregadas" value={furgonData.stats.entregadas} color="green" />
              <MiniStat label="Pendientes" value={furgonData.stats.pendiente_cobro} color="red" />
            </div>

            {/* Payment summary */}
            <div className="bg-white rounded-2xl border border-gray-200 p-3 grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-xs text-gray-400">Valor Total</p>
                <p className="text-sm font-black text-gray-800">${furgonData.stats.total_valor?.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Cobrado</p>
                <p className="text-sm font-black text-green-600">${furgonData.stats.total_cobrado?.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Pendiente</p>
                <p className="text-sm font-black text-red-600">${furgonData.stats.total_pendiente?.toFixed(2)}</p>
              </div>
            </div>

            {/* Facturas list (mobile cards) */}
            <h3 className="font-bold text-gray-700 text-sm">Facturas ({furgonData.facturas.length})</h3>
            <div className="space-y-2" data-testid="dist-facturas-list">
              {furgonData.facturas.map(f => {
                const ei = getEstadoInfo(f.estado_distribucion);
                return (
                  <div key={f.id} className="bg-white rounded-xl border border-gray-200 p-3" data-testid={`dist-factura-${f.numero_factura}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-bold text-indigo-700 text-sm">{f.numero_factura}</p>
                        <p className="text-sm font-semibold text-gray-800 truncate max-w-[180px]">{f.destinatario_nombre}</p>
                        <p className="text-xs text-gray-400">{f.destinatario_ciudad} {f.destinatario_direccion ? `- ${f.destinatario_direccion.slice(0, 30)}` : ''}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-gray-800">${f.total?.toFixed(2)}</p>
                        {f.monto_pendiente > 0 && (
                          <p className="text-xs text-red-600 font-bold">Pend: ${f.monto_pendiente?.toFixed(2)}</p>
                        )}
                      </div>
                    </div>

                    {/* Actions row */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Estado selector */}
                      <Select value={f.estado_distribucion || 'almacen_rd'} onValueChange={(v) => cambiarEstado(f.id, v)}>
                        <SelectTrigger className={`h-8 text-xs font-bold border w-32 ${ei.color}`} data-testid={`estado-select-${f.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ESTADOS.map(e => (
                            <SelectItem key={e.value} value={e.value}>
                              <span className="flex items-center gap-1">{e.icon} {e.label}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {/* Cobrar button */}
                      {f.monto_pendiente > 0 && !f.autorizado_sin_pago && (
                        <Button variant="outline" size="sm"
                          onClick={() => { setCobrarForm({ monto: f.monto_pendiente, metodo_pago_rd: 'efectivo_rd' }); setCobrarDialog({ open: true, factura: f }); }}
                          className="h-8 text-xs border-green-300 text-green-700 gap-1" data-testid={`btn-cobrar-${f.id}`}>
                          <DollarSign className="h-3 w-3" /> Cobrar
                        </Button>
                      )}

                      {/* Call button */}
                      {f.destinatario_telefono && (
                        <a href={`tel:${f.destinatario_telefono}`} className="h-8 px-2 inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-600 rounded-md border border-blue-200 font-semibold">
                          <Phone className="h-3 w-3" /> Llamar
                        </a>
                      )}

                      {f.autorizado_sin_pago && (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-300 text-xs">Autorizada</Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ============== DIALOGS ============== */}

            {/* CONTRATO DIALOG */}
            <Dialog open={contratoDialog} onOpenChange={setContratoDialog}>
              <DialogContent className="max-w-sm" data-testid="dist-contrato-dialog" aria-describedby="dist-contrato-desc">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-base">
                    <FileSignature className="h-5 w-5 text-indigo-600" /> Contrato - {selectedFurgon}
                  </DialogTitle>
                  <p id="dist-contrato-desc" className="text-sm text-gray-500">Lea y firme el contrato de distribucion</p>
                </DialogHeader>
                <div className="space-y-4">
                  {furgonData.contrato.firmado ? (
                    <div className="p-4 bg-green-50 border-2 border-green-300 rounded-xl text-center">
                      <CircleCheck className="h-8 w-8 text-green-600 mx-auto mb-2" />
                      <p className="font-bold text-green-800">Contrato Firmado</p>
                      <p className="text-xs text-green-600">Por: {furgonData.contrato.firmado_por}</p>
                      <p className="text-xs text-green-600">Fecha: {furgonData.contrato.fecha_firma?.slice(0, 10)}</p>
                    </div>
                  ) : (
                    <div className="p-4 bg-red-50 border-2 border-red-300 rounded-xl">
                      <p className="font-bold text-red-800 flex items-center gap-2 mb-2">
                        <AlertOctagon className="h-4 w-4" /> Firma Requerida
                      </p>
                      <p className="text-xs text-red-600">
                        Descargue, lea el contrato completo y acepte los terminos para poder operar con este furgon.
                      </p>
                    </div>
                  )}

                  <Button onClick={handleDescargarContrato} className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 gap-2" data-testid="dist-descargar-contrato">
                    <Download className="h-4 w-4" /> Descargar Contrato PDF
                  </Button>

                  {!furgonData.contrato.firmado && (
                    <Button onClick={handleFirmarContrato} className="w-full h-11 bg-green-600 hover:bg-green-700 gap-2 font-bold" data-testid="dist-firmar-contrato">
                      <FileSignature className="h-4 w-4" /> Acepto - Firmar Digitalmente
                    </Button>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* MI PAGO DIALOG */}
            <Dialog open={pagoDialog} onOpenChange={setPagoDialog}>
              <DialogContent className="max-w-sm" data-testid="dist-pago-dialog" aria-describedby="dist-pago-desc">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-base">
                    <CreditCard className="h-5 w-5 text-purple-600" /> Mi Pago - {selectedFurgon}
                  </DialogTitle>
                  <p id="dist-pago-desc" className="text-sm text-gray-500">Su factura de pago por distribucion</p>
                </DialogHeader>
                <MiPagoContent furgon={selectedFurgon} distApi={distApi} />
              </DialogContent>
            </Dialog>

            {/* COBRAR DIALOG */}
            <Dialog open={cobrarDialog.open} onOpenChange={(o) => setCobrarDialog({ open: o, factura: cobrarDialog.factura })}>
              <DialogContent className="max-w-sm" data-testid="dist-cobrar-dialog" aria-describedby="dist-cobrar-desc">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-base">
                    <DollarSign className="h-5 w-5 text-green-600" /> Registrar Cobro
                  </DialogTitle>
                  <p id="dist-cobrar-desc" className="text-sm text-gray-500">Cobro realizado al destinatario</p>
                </DialogHeader>
                {cobrarDialog.factura && (
                  <div className="space-y-4">
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="font-bold text-blue-800 text-sm">{cobrarDialog.factura.numero_factura}</p>
                      <p className="text-sm text-blue-700">{cobrarDialog.factura.destinatario_nombre}</p>
                      <p className="text-xs text-blue-600">Pendiente: <strong>${cobrarDialog.factura.monto_pendiente?.toFixed(2)}</strong></p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="font-semibold text-sm">Monto</Label>
                        <Input data-testid="dist-cobrar-monto" type="number" step="0.01" min="0"
                          value={cobrarForm.monto || ''} onChange={e => setCobrarForm(p => ({ ...p, monto: parseFloat(e.target.value) || 0 }))}
                          className="h-11 mt-1" />
                      </div>
                      <div>
                        <Label className="font-semibold text-sm">Metodo</Label>
                        <Select value={cobrarForm.metodo_pago_rd} onValueChange={v => setCobrarForm(p => ({ ...p, metodo_pago_rd: v }))}>
                          <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="efectivo_rd">Efectivo</SelectItem>
                            <SelectItem value="transferencia_rd">Transferencia</SelectItem>
                            <SelectItem value="tarjeta_rd">Tarjeta</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button onClick={handleCobrar} className="w-full h-11 bg-green-600 hover:bg-green-700 font-bold" data-testid="dist-submit-cobrar">
                      <DollarSign className="h-4 w-4 mr-2" /> Registrar Cobro
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </div>
  );
};

const MiniStat = ({ label, value, color }) => {
  const colors = {
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    red: 'bg-red-50 border-red-200 text-red-700',
  };
  return (
    <div className={`rounded-xl border p-2.5 text-center ${colors[color]}`}>
      <p className="text-xs font-bold opacity-70">{label}</p>
      <p className="text-xl font-black">{value || 0}</p>
    </div>
  );
};

const MiPagoContent = ({ furgon, distApi }) => {
  const [pago, setPago] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await distApi.get(`/distribuidor/furgon/${furgon}/mi-pago`);
        setPago(res.data);
      } catch { /* no invoice yet */ }
      setLoading(false);
    };
    load();
  }, [furgon, distApi]);

  if (loading) return <div className="text-center py-6 text-gray-400">Cargando...</div>;

  if (!pago) return (
    <div className="text-center py-6">
      <CreditCard className="h-10 w-10 text-gray-300 mx-auto mb-2" />
      <p className="text-gray-500 text-sm">No hay factura de pago asignada aun</p>
      <p className="text-gray-400 text-xs">Contacte al administrador</p>
    </div>
  );

  return (
    <div className="space-y-3">
      <div className={`p-3 rounded-lg border-2 font-semibold text-center ${pago.estado === 'pagado' ? 'bg-green-50 border-green-300 text-green-800' : 'bg-amber-50 border-amber-300 text-amber-800'}`}>
        <p className="text-sm">{pago.numero_factura}</p>
        <p className="text-2xl font-black">${(pago.total || 0).toFixed(2)}</p>
        <Badge className={pago.estado === 'pagado' ? 'bg-green-200 text-green-800' : 'bg-amber-200 text-amber-800'}>
          {pago.estado?.toUpperCase()}
        </Badge>
      </div>
      {pago.conceptos?.length > 0 && (
        <div className="space-y-1">
          {pago.conceptos.map((c, i) => (
            <div key={i} className="flex justify-between text-sm p-2 bg-gray-50 rounded">
              <span className="text-gray-600">{c.descripcion}</span>
              <span className="font-bold">${(c.subtotal || 0).toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DistribuidorPortal;
